import React from "react";

// ---------------------------------------------------------------------------
// Scenario sliders — Time (T+N decay) + Spot Shock (±%) + IV Shock (vol pts).
// Designed to feel like OptionStrat's "what-if" inspector: drag time forward
// to watch theta carve into the curve; drag shock to see P&L at a stressed
// spot price; drag IV shock to approximate vol crush / spike via vega.
// ---------------------------------------------------------------------------
export const ScenarioSliders = ({
  timeFrac, setTimeFrac, shockPct, setShockPct,
  ivShockVp, setIvShockVp, vega,
  tnCurves, spot, shockedSpot, shockPnl, activeDaysForward,
}) => {
  const maxDays = tnCurves?.[tnCurves.length - 1]?.days_forward || 0;
  const currentDays = activeDaysForward != null
    ? Math.round(activeDaysForward * 10) / 10
    : 0;
  const isAtExpiry = timeFrac >= 0.999;
  const ivShockAvailable = vega != null && Math.abs(vega) > 0.0001;
  const ivShockDollar = ivShockAvailable ? vega * (ivShockVp || 0) : 0;

  return (
    <div
      className="mt-3 rounded-lg border border-white/10 bg-[#050505]/55 px-3 py-2.5"
      data-testid="payoff-scenario-sliders"
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] uppercase tracking-[0.22em] text-white/45">
          Scenario inspector
        </span>
        {(timeFrac !== 0 || shockPct !== 0 || ivShockVp !== 0) && (
          <button
            type="button"
            onClick={() => { setTimeFrac(0); setShockPct(0); setIvShockVp?.(0); }}
            data-testid="payoff-scenario-reset"
            className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded border border-white/15 text-white/55 hover:text-white hover:border-white/30 transition"
          >
            Reset
          </button>
        )}
      </div>

      {/* Time / DTE slider */}
      <div className="mb-3" data-testid="payoff-time-slider-wrap">
        <div className="flex items-baseline justify-between mb-1">
          <label
            htmlFor="payoff-time-slider"
            className="text-[10.5px] text-white/60 inline-flex items-center gap-1.5"
          >
            <span className="text-[#00E5FF]">⏱</span> Time
            <span className="text-white/35 italic ml-1">
              today → expiry
            </span>
          </label>
          <span
            className={
              "text-[11px] num font-medium px-1.5 py-0.5 rounded " +
              (isAtExpiry
                ? "text-[#FFD60A] bg-[#FFD60A]/12 border border-[#FFD60A]/35"
                : timeFrac > 0
                  ? "text-[#00E5FF] bg-[#00E5FF]/12 border border-[#00E5FF]/35"
                  : "text-white/65 bg-white/5 border border-white/12")
            }
            data-testid="payoff-time-readout"
          >
            {isAtExpiry
              ? `Expiry · T+${Math.round(maxDays)}d`
              : timeFrac === 0
                ? "Today · T+0"
                : `T+${currentDays}d`}
          </span>
        </div>
        <input
          id="payoff-time-slider"
          type="range"
          min={0}
          max={1}
          step={0.005}
          value={timeFrac}
          onChange={(e) => setTimeFrac(parseFloat(e.target.value))}
          data-testid="payoff-time-slider"
          aria-label={`Time forward — ${currentDays} days from now`}
          aria-valuemin={0}
          aria-valuemax={maxDays || 0}
          aria-valuenow={currentDays}
          aria-valuetext={`T+${currentDays} days`}
          className="w-full accent-[#00E5FF] h-1.5"
        />
        <div className="flex items-center justify-between text-[9px] text-white/30 mt-0.5 font-mono">
          <span>T+0</span>
          {tnCurves?.length > 2 && (
            <span>T+{Math.round(maxDays / 2)}d</span>
          )}
          <span>T+{Math.round(maxDays)}d</span>
        </div>
      </div>

      {/* Shock slider — vertical line + readout pulls from active curve */}
      <div data-testid="payoff-shock-slider-wrap">
        <div className="flex items-baseline justify-between mb-1">
          <label
            htmlFor="payoff-shock-slider"
            className="text-[10.5px] text-white/60 inline-flex items-center gap-1.5"
          >
            <span className="text-[#FF7A33]">⚡</span> Shock
            <span className="text-white/35 italic ml-1">
              spot ± move
            </span>
          </label>
          <span
            className={
              "text-[11px] num font-medium px-1.5 py-0.5 rounded " +
              (Math.abs(shockPct) > 0.001
                ? (shockPnl ?? 0) >= 0
                  ? "text-[#00C805] bg-[#00C805]/12 border border-[#00C805]/35"
                  : "text-[#FF3B30] bg-[#FF3B30]/12 border border-[#FF3B30]/35"
                : "text-white/65 bg-white/5 border border-white/12")
            }
            data-testid="payoff-shock-readout"
          >
            {Math.abs(shockPct) < 0.001
              ? "No shock"
              : `${shockPct >= 0 ? "+" : ""}${(shockPct * 100).toFixed(1)}% → $${shockedSpot?.toFixed(2)} · ${(shockPnl ?? 0) >= 0 ? "+" : ""}$${Math.round(shockPnl ?? 0)}`}
          </span>
        </div>
        <input
          id="payoff-shock-slider"
          type="range"
          min={-0.25}
          max={0.25}
          step={0.001}
          value={shockPct}
          onChange={(e) => setShockPct(parseFloat(e.target.value))}
          data-testid="payoff-shock-slider"
          aria-label={`Spot shock — ${(shockPct * 100).toFixed(1)}% from current`}
          aria-valuemin={-25}
          aria-valuemax={25}
          aria-valuenow={Math.round(shockPct * 1000) / 10}
          aria-valuetext={`${(shockPct * 100).toFixed(1)} percent`}
          className="w-full accent-[#FF7A33] h-1.5"
        />
        <div className="flex items-center justify-between text-[9px] text-white/30 mt-0.5 font-mono">
          <span>-25%</span>
          <span>spot</span>
          <span>+25%</span>
        </div>
      </div>

      {/* IV shock — vega-based linear approximation of vol crush/spike. */}
      {ivShockAvailable && (
        <div className="mt-3" data-testid="payoff-iv-shock-wrap">
          <div className="flex items-baseline justify-between mb-1">
            <label
              htmlFor="payoff-iv-shock-slider"
              className="text-[10.5px] text-white/60 inline-flex items-center gap-1.5"
            >
              <span className="text-[#3FE5DC]">σ</span> IV Shock
              <span className="text-white/35 italic ml-1">
                vol crush / spike
              </span>
            </label>
            <span
              className={
                "text-[11px] num font-medium px-1.5 py-0.5 rounded " +
                (Math.abs(ivShockVp || 0) > 0.001
                  ? ivShockDollar >= 0
                    ? "text-[#00C805] bg-[#00C805]/12 border border-[#00C805]/35"
                    : "text-[#FF3B30] bg-[#FF3B30]/12 border border-[#FF3B30]/35"
                  : "text-white/65 bg-white/5 border border-white/12")
              }
              data-testid="payoff-iv-shock-readout"
            >
              {Math.abs(ivShockVp || 0) < 0.001
                ? "No vol shock"
                : `${ivShockVp >= 0 ? "+" : ""}${ivShockVp.toFixed(0)} vol pts · ${ivShockDollar >= 0 ? "+" : ""}$${Math.round(ivShockDollar)}`}
            </span>
          </div>
          <input
            id="payoff-iv-shock-slider"
            type="range"
            min={-15}
            max={15}
            step={1}
            value={ivShockVp || 0}
            onChange={(e) => setIvShockVp?.(parseFloat(e.target.value))}
            data-testid="payoff-iv-shock-slider"
            aria-label={`IV shock — ${ivShockVp} vol points`}
            aria-valuemin={-15}
            aria-valuemax={15}
            aria-valuenow={ivShockVp || 0}
            aria-valuetext={`${ivShockVp} vol points`}
            className="w-full accent-[#3FE5DC] h-1.5"
          />
          <div className="flex items-center justify-between text-[9px] text-white/30 mt-0.5 font-mono">
            <span>-15 vp</span>
            <span>flat</span>
            <span>+15 vp</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScenarioSliders;
